import React, { useState } from 'react';
import {
  ActionList,
  DropdownOverlay,
  Dropdown,
  ActionListItem,
  ActionListItemBadge,
  SelectInputProps,
  AutoComplete,
  SelectInput,
} from '@razorpay/blade/components';
import { SalaryStructure } from 'components/SalaryStructure/types';
import { useDebouncedValue } from 'hooks/useDebouncedValue';

const SalaryStructureDropDown = ({
  structures = [],
  registerProps,
  label = 'Salary Structure',
  placeholder,
  hasAutoComplete = false,
  required = false,
}: {
  registerProps: Pick<
    SelectInputProps,
    'name' | 'onChange' | 'errorText' | 'validationState' | 'value' | 'isDisabled' | 'onFocus'
  >;
  required?: boolean;
  onChange?: (val: string) => void;
  structures?: SalaryStructure[];
  label?: string;
  placeholder?: string;
  hasAutoComplete?: boolean;
}) => {
  const [search, setSearchValue] = useState('');
  const { debouncedValue: debouncedSearchValue } = useDebouncedValue(search);

  const filteredSalaryStructures = React.useMemo(() => {
    if (!structures) return [];

    if (!debouncedSearchValue.trim() || !hasAutoComplete) {
      return structures;
    }

    return structures.filter((structure) => {
      const searchTerm = debouncedSearchValue.toLowerCase();
      return (
        structure.name?.toLowerCase().includes(searchTerm) ||
        structure.description?.toLowerCase().includes(searchTerm)
      );
    });
  }, [structures, debouncedSearchValue]);

  return (
    <Dropdown selectionType="single">
      {!hasAutoComplete ? (
        <SelectInput
          isRequired={required}
          necessityIndicator={required ? 'required' : undefined}
          {...registerProps}
          label={label}
          placeholder={placeholder}
        />
      ) : (
        <AutoComplete
          label="Salary Structure"
          placeholder="Select salary structure"
          onInputValueChange={({ name, value }) => {
            setSearchValue(value || '');
          }}
          filteredValues={filteredSalaryStructures.map((structure) => structure.id)}
          {...registerProps}
          value={registerProps.value?.[0]}
          isRequired={required}
          necessityIndicator={required ? 'required' : undefined}
        />
      )}
      <DropdownOverlay>
        <ActionList>
          {filteredSalaryStructures.map((item) => (
            <ActionListItem
              description={item.description}
              titleSuffix={
                item.type === 'SALARY_STRUCTURE_TYPE_DEFAULT' ? (
                  <ActionListItemBadge color="information">
                    Organisation Default
                  </ActionListItemBadge>
                ) : undefined
              }
              key={item.id}
              title={item.name}
              value={item.id}
            />
          ))}
        </ActionList>
      </DropdownOverlay>
    </Dropdown>
  );
};

export default SalaryStructureDropDown;
